/* global runtime */
// Bygging/lagring av faktura-, annet salg- og kjøpseditor, anleggsmiddel-reklassifisering, PDF/årsdokumenter og ansatt-/selskapsdokumenter.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

const projectCostFields = (source) => window.PlummeProjectCostLink?.normalizeProjectCostFields(source) || {};
const parseSequentialInvoiceNumber = (value) => {
  const text = String(value || '').trim();
  return /^\d+$/.test(text) ? Number(text) : null;
};
const nextSequentialInvoiceNumber = (state, invoiceId) => {
  const numbers = safeArray(state.invoices)
    .filter((entry) => entry.id !== invoiceId)
    .map((entry) => parseSequentialInvoiceNumber(entry.invoiceNumber))
    .filter((value) => Number.isSafeInteger(value) && value > 0);
  const maxExisting = numbers.length ? Math.max(...numbers) : 0;
  const counter = Number(state.invoiceNumberCounter);
  const counterStart = Number.isSafeInteger(counter) && counter > 0 ? counter : 0;
  return String(Math.max(1, maxExisting + 1, counterStart));
};
const ensureSequentialInvoiceNumber = (invoice, state) => {
  if (!invoice || invoice.status === 'draft' || invoice.invoiceNumber) return invoice;
  return {
    ...invoice,
    invoiceNumber: nextSequentialInvoiceNumber(state, invoice.id),
  };
};

// Bankkontoen kunden betaler til avgjor hvilken hovedbokskonto innbetalingen skal
// foeres paa naar fakturaen registreres som betalt. Kontonummeret vises paa
// dokumentet, hovedbokskoden brukes i bokforingen.
const invoiceBankLedgerCode = (accountNumber) => {
  const number = String(accountNumber || '').trim();
  if (!number) return '';
  const settings = runtime.appState?.settings || {};
  const match = safeArray(settings.bankAccounts).find((account) => String(account.accountNumber || '').trim() === number);
  if (match) return getLedgerAccountCode(match, '1920');
  if (String(settings.bankAccount || '').trim() === number) return String(settings.bankAccountingAccount || '1920').trim();
  return '';
};

// Fingeravtrykk av det som ville blitt lagret. Brukes til aa vite om utkastet i
// editoren allerede ligger lagret slik det staar — da skal «Lagre utkast» vaere
// avslaatt, ikke lage enda et utkast av det samme.
//
// Nokkelen bygges av selve lagringspayloaden, ikke av en handplukket liste med
// felt. En glemt linje i en slik liste ville gitt den verste feilen her: en
// endring som ikke kan lagres fordi knappen tror alt er lagret.
const editorDraftKey = (editor) => {
  if (!editor) return '';
  try {
    const payload = editor.docType === 'annet'
      ? buildOtherSaleFromEditor(editor, 'draft')
      : buildInvoiceFromEditor(editor, 'draft');
    // Rest-destrukturering er forbudt her: Babel-standalone lager en `_excluded`
    // per fil, og de kolliderer i det delte globale skopet (se
    // scripts/check-runtime-scripts.mjs).
    const rest = { ...(payload || {}) };
    delete rest.id;
    delete rest.createdAt;
    delete rest.updatedAt;
    // Vedleggene legges paa etter opplasting og finnes derfor ikke i payloaden.
    // Uten dem ville et nytt vedlegg ikke telle som en endring.
    const attachments = safeArray(editor.attachments)
      .map((attachment) => `${attachment?.name || ''}:${attachment?.size || ''}`)
      .join('|');
    return JSON.stringify([rest, attachments]);
  } catch (error) {
    // Et ufullstendig utkast skal kunne lagres. Uten noekkel er knappen paa.
    return '';
  }
};

const invoiceDraftExists = (invoiceId) => {
  const id = String(invoiceId || '');
  if (!id) return false;
  return safeArray(runtime.appState?.invoices).some((invoice) => invoice.id === id)
    || safeArray(runtime.appState?.otherSales).some((sale) => sale.id === id);
};
const editorLineType = (line) => String(line?.lineType || line?.documentLineType || 'product');
const editorLineHasAmount = (line) => editorLineType(line) === 'product';
const buildInvoiceFromEditor = (editor, status) => {
  const country = currentCountry();
  const kind = uiTypeToInvoiceKind(editor.docType);
  const invoiceDate = editor.invDate || todayIso();
  const dueDate = editor.dueDate || window.PlummeInvoicePaymentTerms.dueDate(invoiceDate, runtime.appState?.settings);
  const customer = editor.customer || (editor.newName ? { name: editor.newName } : null);
  const customerIds = Array.from(new Set(safeArray(customer?.customerIds || editor.customerIds)
    .map((id) => String(id || '').trim())
    .filter(Boolean)));
  const hasProduct = safeArray(editor.lines).some((line) => editorLineHasAmount(line) && String(line.desc || line.description || '').trim());
  if (status !== 'draft' && !customer?.name) throw new Error(t('svc.pickCustomerForDoc'));
  if (status !== 'draft' && !hasProduct) throw new Error(t('svc.addProductForDoc'));
  const existing = editor.sourceDraftId
    ? safeArray(runtime.appState?.invoices).find((invoice) => invoice.id === editor.sourceDraftId)
    : null;
  const hasRentalLine = safeArray(editor.lines).some((line) => editorLineHasAmount(line) && (line.type === 'Utleie' || line.productKind === 'rental'));
  const hasDeliveryPeriod = (kind === 'offer' || kind === 'order_confirmation') && hasRentalLine;
  const serviceDate = hasDeliveryPeriod ? (editor.deliveryFrom || editor.serviceDate || invoiceDate) : existing?.serviceDate;
  const serviceEndDate = hasDeliveryPeriod ? (editor.deliveryTo || editor.serviceEndDate || serviceDate) : existing?.serviceEndDate;
  if (serviceDate && serviceEndDate && serviceEndDate < serviceDate) throw new Error(t('svc.toDateBeforeFrom'));
  const id = existing?.id || editor.sourceDraftId || `inv${Date.now()}`;
  const invoiceBankAccountNumber = String(editor.invoiceBankAccount || '').trim() || existing?.bankAccount || undefined;
  return {
    ...(existing || {}),
    id,
    invoiceNumber: status === 'draft' ? (existing?.invoiceNumber || '') : (existing?.invoiceNumber || ''),
    customer: {
      id: customer?.id || customer?.customerId || undefined,
      name: customer?.name || 'Uten kunde',
      customerNumber: customer?.nr || customer?.customerNumber || '',
      number: customer?.nr || customer?.customerNumber || '',
      organizationNumber: customer?.org || customer?.organizationNumber || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      address: customer?.addr || customer?.address || '',
      postalCode: customer?.postalCode || '',
      postalPlace: customer?.postalPlace || '',
      defaultCustomerReference: customer?.defaultCustomerReference || '',
      isList: customerIds.length ? true : undefined,
    },
    customerId: customer?.id || customer?.customerId || undefined,
    customerIds: customerIds.length ? customerIds : undefined,
    date: invoiceDate,
    // Kreditnotaen forfaller ikke — den skal ikke betales av kunden. Feltet
    // settes likevel til selve kreditnotadatoen, ikke fjernes, slik at lister og
    // sorteringer som leser `dueDate` fortsatt har en dato aa holde seg til.
    dueDate: kind === 'credit' ? invoiceDate : dueDate,
    validUntil: kind === 'offer' ? dueDate : undefined,
    serviceDate,
    serviceEndDate,
    // Kopien fra et tilbud er ulagret naar den ligger i editoren, saa pekeren
    // kommer derfra. `existing` daekker utkast som allerede er lagret en gang.
    convertedFromInvoiceId: editor.convertedFromInvoiceId || existing?.convertedFromInvoiceId || undefined,
    fixedAssetSale: editor.fixedAssetSale || existing?.fixedAssetSale,
    // Fri kreditnota fra salgseditoren. Pekeren til fakturaen er valgfri: er den
    // satt, motregner `creditNoteSettlement` kreditnotaen mot fakturaens aapne
    // belop; er den tom, blir belopet staaende som kundens tilgodebelop.
    // Aarsak og referanse er dokumentasjonen bokfoeringsforskriften § 5-2-7 krever
    // naar et salg reverseres, og de foelger dokumentet ut i PDF-en.
    creditedFromInvoiceId: kind === 'credit'
      ? (String(editor.creditedFromInvoiceId || '').trim() || existing?.creditedFromInvoiceId || undefined)
      : existing?.creditedFromInvoiceId,
    creditReason: kind === 'credit' ? (String(editor.creditReason || '').trim() || undefined) : existing?.creditReason,
    creditReference: kind === 'credit' ? (String(editor.creditReference || '').trim() || undefined) : existing?.creditReference,
    // Editoren legger paa foretaksdokumentene selv, og brukeren kan fjerne dem for
    // den ene sendingen. Uten dette flagget ville backend lagt dem paa igjen.
    companyDocumentsAppliedByEditor: kind === 'credit' ? true : existing?.companyDocumentsAppliedByEditor,
    reference: editor.reference || '',
    ourReference: editor.reference || '',
    theirReference: editor.theirRef || '',
    customerReference: editor.theirRef || '', currency: String(editor.currency || ({ NO: 'NOK', SE: 'SEK', DK: 'DKK' }[country] || 'NOK')).toUpperCase(), accountingCurrency: String(editor.accountingCurrency || ({ NO: 'NOK', SE: 'SEK', DK: 'DKK' }[country] || 'NOK')).toUpperCase(),
    exchangeRate: num(editor.exchangeRate, 1), exchangeRateDate: editor.exchangeRateDate || invoiceDate, exchangeRateSource: editor.exchangeRateSource || (String(editor.currency || '').toUpperCase() === String(editor.accountingCurrency || '').toUpperCase() ? 'accounting-currency' : undefined),
    // Beskrivelsen staar over linjetabellen paa dokumentet, kommentaren i boksen
    // nederst. De ble tidligere slaatt sammen til ett felt, og da forsvant
    // beskrivelsen naar begge var fylt ut.
    description: editor.descr || '',
    comment: editor.comment || '',
    // Bankkontoen kunden skal betale til. Bare relevant for faktura; tilbud og
    // ordrebekreftelse har ingen betalingsinformasjon paa dokumentet.
    bankAccount: kind === 'invoice' ? invoiceBankAccountNumber : undefined,
    bankAccountCode: kind === 'invoice' ? (invoiceBankLedgerCode(invoiceBankAccountNumber) || existing?.bankAccountCode || undefined) : undefined, documentDiscountPercent: Math.max(0, Math.min(100, num(editor.totalDiscountPercent))), ...(kind === 'invoice' ? window.PlummeBankAccountSettings.invoicePaymentSnapshotFields(runtime.appState?.settings?.bankAccounts, invoiceBankAccountNumber, existing) : {}),
    lines: safeArray(editor.lines).map((line, index) => {
      const lineType = editorLineType(line);
      if (lineType !== 'product') return stripUndefinedDeep({
        id: String(line.id || `line${index}`),
        lineType,
        documentLineType: lineType,
        savedTextLineId: line.savedTextLineId || undefined,
        description: lineType === 'spacer' ? '' : (line.desc || line.description || line.name || ''),
        note: lineType === 'spacer' ? undefined : (line.note || line.text || undefined),
        nameStyle: line.nameStyle || undefined,
        textStyle: line.textStyle || undefined,
        showDivider: lineType === 'spacer' ? undefined : line.showDivider !== false,
        bold: line.nameStyle?.bold === true || line.bold === true || undefined,
        italic: line.nameStyle?.italic === true || line.italic === true || undefined,
      });
      return window.PlummeInvoicePurchaseFields.applyInvoiceDocumentDiscount(editorProductLineToBackend(line, index, { country, editor, invoiceDate }), editor.totalDiscountPercent);
    }),
    attachments: existing?.attachments || undefined,
    createdAt: existing?.createdAt || nowIso(),
    updatedAt: nowIso(),
    kind,
    status,
    // `null`, ikke `undefined`: et felt som mangler i payloaden overskriver
    // ingenting i sammenslaaingen paa serveren, og et utkast som ble opprettet
    // ville da beholdt utkastflagget sitt.
    invoiceType: status === 'draft' ? 'draft' : null,
    recurring: kind === 'invoice' && editor.repeating ? {
      enabled: true,
      interval: recurringIntervalFromEditor(editor.repeatInterval),
      dueDays: Math.max(0, Math.round(num(editor.repeatDue, 14))),
    } : undefined,
    sentAt: status === 'draft' ? undefined : existing?.sentAt,
  };
};

const upsertInvoice = async (invoice) => {
  if (!invoice?.id) throw new Error(t('svc.missingInvoiceToSave'));
  assertCreditInvoiceMutationAllowed(invoice);
  const result = await authorizedPost('upsertInvoiceHttp', {
    businessId: runtime.activeBusinessId,
    invoice,
  });
  if (result.invoice) {
    if (result.state) { runtime.appState = mergeAppMutationState(result.state); emitRuntime(); }
    replaceInvoiceLocal(result.invoice);
    // En innbetaling kan alt ligge og vente i banken; fakturaen var det som manglet
    // for at treffet kunne gjores. Regelbasert og gratis — men etterarbeid, saa den
    // ventes ikke paa: paa kritisk vei kom kvitteringsmodalen sekunder for sent.
    scheduleBankSuggestionRulesRefresh();
    return result.invoice;
  }
  throw new Error(t('svc.backendDidNotSave'));
};

const vatTermLockForDate = (state, date) => {
  if (!state?.settings?.vatRegistered) return null;
  const parsed = new Date(`${String(date || '').slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const period = window.PlummeVatPeriodHistory?.forDate?.(state.settings, String(date || '').slice(0,10)) || state.settings?.vatPeriod || 'bimonthly';
  const deadlineStatus = state.deadlinesByYear?.[String(year)] || {};
  if (period === 'annual') return deadlineStatus.mvaSubmitted ? {year,key:'annual'} : null;
  const index = Math.floor(parsed.getMonth() / (period === 'monthly' ? 1 : period === 'quarterly' ? 3 : period === 'semiannual' ? 6 : 2)) + 1;
  const key = `term-${index}`;
  return deadlineStatus.mvaTerms?.[key] || deadlineStatus.mvaTerms?.[`${year}-${index}`] ? {year,key} : null;
};
const yearClosingLockForDate = (state, date) => {
  const parsed = new Date(`${String(date || '').slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const yearKey = String(parsed.getFullYear());
  const deadlineStatus = state?.deadlinesByYear?.[yearKey] || {};
  const process = state?.yearClosingProcessByYear?.[yearKey] || {};
  return state?.closedYearsByYear?.[yearKey] || process.closedAt || deadlineStatus.annualClosingSubmitted
    ? { year: Number(yearKey) }
    : null;
};
const lockedBankStatementForDate = (state, date) => {
  const dateKey = String(date || '').slice(0, 10);
  if (!dateKey) return null;
  return safeArray(state?.bankStatements).find((statement) => {
    if (statement.source && statement.source !== 'business') return false;
    const locked = Boolean(statement.bookedAt || statement.reconciledAt || ['archived','reconciled','booked'].includes(String(statement.status || '')));
    return locked && statement.periodFrom <= dateKey && dateKey <= statement.periodTo;
  }) || null;
};
const isBusinessBankPayment = (payment) => {
  if(payment?.settlementKind === 'no_payment') return false;
  const code = String(payment?.settlementAccountCode || '').trim();
  const source = String(payment?.paymentSource || payment?.paidBy || '').trim();
  return !(source === 'private' || source === 'cash' || source === 'loan' || source === 'company_card' || source === 'payment_solution' || /^238\d$/.test(code) || /^158\d$/.test(code) || ['2060','1900','2250','2400'].includes(code));
};
const assertVatOpenForDate = (state, date) => {
  if (!vatTermLockForDate(state, date)) return;
  throw new Error(t('svc.vatPeriodFiled'));
};
const throwLockedPurchaseVatChange = () => {
	  throw new Error(t('r1e.purchaseEditor.vatLocked'));
};
const assertYearOpenForDate = (state, date) => {
  if (!yearClosingLockForDate(state, date)) return;
  throw new Error(t('svc.yearClosed'));
};
const assertBankOpenForPayments = (state, payments) => {
  const locked = safeArray(payments).find((payment) => isBusinessBankPayment(payment) && lockedBankStatementForDate(state, payment.date));
  if (!locked) return;
  throw new Error(t('svc.bankPeriodReconciled'));
};
const purchaseVatSignature = (expense) => JSON.stringify({
  date: expense?.date || '',
  amountNet: num(expense?.amountNet),
  vatRate: num(expense?.vatRate),
  vatTreatment: expense?.vatTreatment || '',
  purchaseVatCode: expense?.purchaseVatCode || '',
  standardVatCode: expense?.standardVatCode || '',
  vatDeductionMode: expense?.vatDeductionMode || 'full',
  reverseChargeCode: expense?.reverseChargeCode || '',
  lines: (safeArray(expense?.purchaseLines).length ? safeArray(expense?.purchaseLines) : [expense]).map((line)=>({
    amountNet:num(line.amountNet),
    vatRate:num(line.vatRate),
    vatTreatment:line.vatTreatment || '',
    purchaseVatCode:line.purchaseVatCode || '',
    standardVatCode:line.standardVatCode || '',
    reverseChargeCode:line.reverseChargeCode || '', vatDeductionMode:line.vatDeductionMode || expense?.vatDeductionMode || 'full', deductibleVatPercent:num(line.deductibleVatPercent, expense?.deductibleVatPercent ?? 100),
  })),
});
const purchaseBankPaymentSignature = (expense, payments) => JSON.stringify(safeArray(payments).filter(isBusinessBankPayment).map((payment)=>({
  amount:num(payment.amount),
  date:payment.date || '',
  settlementAccountCode:payment.settlementAccountCode || '',
  status:payment.status || 'active',
})).concat([{
  supplierInvoicePaidAt: expense?.supplierInvoicePaidAt || '',
  paidAt: expense?.paidAt || '',
  settlementAccountCode: expense?.settlementAccountCode || '',
}]));
const purchaseFixedAssetSignature = (expense) => JSON.stringify(expense?.fixedAsset ? { assetGroup:expense.fixedAsset.assetGroup||'',readyForUseDate:expense.fixedAsset.readyForUseDate||'',usefulLifeMonths:num(expense.fixedAsset.usefulLifeMonths),residualValue:num(expense.fixedAsset.residualValue),depreciationMethod:expense.fixedAsset.depreciationMethod||'',taxDepreciationRate:num(expense.fixedAsset.taxDepreciationRate),status:expense.fixedAsset.status||'',soldAt:expense.fixedAsset.soldAt||'',saleAmountNet:num(expense.fixedAsset.saleAmountNet),impairmentAssessments:safeArray(expense.fixedAsset.impairmentAssessments) }:null); const purchaseBusinessBankPaymentsFromExpense = (expense) => {
  if(!expense) return [];
  const gross = dashboardGrossExpense(expense);
  const splits = safeArray(expense.paymentSplits).filter((split)=>split.settlementAccountCode !== '2400' && num(split.amount)>0);
  if(splits.length) return splits.map((split)=>({
    amount: Math.min(num(split.amount), gross),
    date: split.date || expense.supplierInvoicePaidAt || expense.paidAt || expense.date,
    settlementAccountCode: purchaseSettlementAccountCode(split.settlementAccountCode || expense.settlementAccountCode),
    paymentSource: normalizePurchasePaymentSource(split.settlementAccountCode || expense.settlementAccountCode),
  }));
  if(expense.supplierInvoicePaidAt || (!expense.isSupplierInvoice && expense.settlementAccountCode)) return [{
    amount: gross,
    date: expense.supplierInvoicePaidAt || expense.paidAt || expense.date,
    settlementAccountCode: purchaseSettlementAccountCode(expense.settlementAccountCode),
    paymentSource: normalizePurchasePaymentSource(expense.settlementAccountCode),
  }];
  return [];
};
const assertPurchaseMutationAllowed = ({previousExpense,nextExpense,action}) => {
  const state = mergeState(runtime.appState);
  const previousPayments = safeArray(state.payments).filter((payment)=>payment.documentType === 'supplier_invoice' && payment.documentId === previousExpense?.id && payment.status !== 'reversed');
  if(action === 'annul'){
    assertYearOpenForDate(state, previousExpense?.date);
    assertVatOpenForDate(state, previousExpense?.date);
    assertBankOpenForPayments(state, previousPayments);
    return;
  }
  const nextPayments = purchaseBusinessBankPaymentsFromExpense(nextExpense);
  if(yearClosingLockForDate(state, previousExpense?.date) || yearClosingLockForDate(state, nextExpense?.date)){
    if(!previousExpense || purchaseVatSignature(previousExpense) !== purchaseVatSignature(nextExpense) || purchaseFixedAssetSignature(previousExpense) !== purchaseFixedAssetSignature(nextExpense)) assertYearOpenForDate(state, previousExpense?.date || nextExpense?.date);
  }
  if(vatTermLockForDate(state, previousExpense?.date || nextExpense?.date) || vatTermLockForDate(state, nextExpense?.date)){
    if(!previousExpense || purchaseVatSignature(previousExpense) !== purchaseVatSignature(nextExpense)) throwLockedPurchaseVatChange();
  }
  if(previousPayments.some((payment)=>isBusinessBankPayment(payment)&&lockedBankStatementForDate(state,payment.date))){
    if(purchaseBankPaymentSignature(previousExpense, previousPayments) !== purchaseBankPaymentSignature(nextExpense, nextPayments)) assertBankOpenForPayments(state, previousPayments);
  }
  assertBankOpenForPayments(state, nextPayments);
};
const assertSimpleVatMutationAllowed = ({previousDate,nextDate,vatAffects=true,vatPeriodAffects=vatAffects}) => {
  const state = mergeState(runtime.appState);
  if(!vatAffects) return;
  assertYearOpenForDate(state, previousDate); assertYearOpenForDate(state, nextDate);
  if(vatPeriodAffects) { assertVatOpenForDate(state, previousDate); assertVatOpenForDate(state, nextDate); }
};
const assertCreditInvoiceMutationAllowed = (invoice) => {
  if(invoice?.kind !== 'credit' || !invoice.creditedFromInvoiceId) return;
  const state = mergeState(runtime.appState);
  const source = safeArray(state.invoices).find((entry)=>entry.id === invoice.creditedFromInvoiceId);
  if(!source) return;
  // New posting locks use the credit date; the source keeps its VAT lock.
  assertYearOpenForDate(state, invoice.date); assertVatOpenForDate(state, invoice.date); assertVatOpenForDate(state, source.date);
};

const editorSettlementAccountCode = (value, fallback = '1920') => {
  const code = String(value || '').trim();
  return /^\d{4}$/.test(code) ? code : fallback;
};

const buildOtherSaleFromEditor = (editor, status = 'completed') => {
  const country = currentCountry();
  const hasLine = safeArray(editor.lines).some((line) => editorLineHasAmount(line) && String(line.desc || '').trim());
  const hasIncompleteLine = safeArray(editor.lines).some((line) => editorLineHasAmount(line) && !String(line.desc || line.description || '').trim());
  if (status !== 'draft' && hasIncompleteLine) throw new Error(t('r1e.purchaseEditor.descriptionRequired'));
  const hasAttachment = safeArray(editor.attachments).length > 0;
  if (status !== 'draft' && !hasLine && !hasAttachment) throw new Error(t('svc.addLineOrVoucherOther'));
  const firstLine = safeArray(editor.lines).find((line) => editorLineHasAmount(line) && String(line.desc || '').trim()) || {};
  const existing = editor.sourceDraftId
    ? safeArray(runtime.appState?.otherSales).find((sale) => sale.id === editor.sourceDraftId)
    : null;
  const id = existing?.id || editor.sourceDraftId || `otherSale${Date.now()}`;
  // Alle linjer lagres med netto etter rabatt; foringen skjer per sats og konto.
  const lines = otherSaleEditorLines(editor, country);
  const amountNet = voucherMoney(lines.reduce((sum, line) => sum + line.amountNet, 0));
  const vat = lines[0] ? { rate: lines[0].vatRate, treatment: lines[0].vatTreatment, reportCode: lines[0].reportVatCode } : normalizeSalesVatOption(firstLine.vat, country);
  const requestedSettlementAccountCode = String(editor.payAccount || editor.paidTo || '').trim(); if (status !== 'draft' && !requestedSettlementAccountCode) throw new Error(t('sale.create.needPaymentAccount'));
  const paymentSource = requestedSettlementAccountCode ? normalizeOtherSalePaymentSource(editor.paymentSource ?? editor.paidTo) : undefined;
  const settlementAccountCode = requestedSettlementAccountCode ? editorSettlementAccountCode(requestedSettlementAccountCode, otherSalePaymentMeta(paymentSource).code) : undefined;
  const payment = otherSaleEditorPayment(editor, { sale: { lines, amountNet }, paymentSource, settlementAccountCode, status });
  return stripUndefinedDeep({
    ...(existing || {}),
    id,
    voucherNumber: existing?.voucherNumber || (status === 'draft' ? '' : otherSaleVoucherNumberFromId(id)),
    date: editor.invDate || todayIso(),
    description: editor.descr || firstLine.desc || 'Annet salg', fixedAssetSale: editor.fixedAssetSale || existing?.fixedAssetSale,
    amountNet,
    lines,
    vatRate: vat.rate,
    vatTreatment: vat.treatment,
    reportVatCode: vat.reportCode || undefined,
    salesAccountCode: lines[0]?.salesAccountCode || firstLine.salesAccountCode || (['salesReverseCharge51', 'export52', 'zeroRated', 'exempt'].includes(vat.treatment) ? '3100' : '3000'),
    customerId: editor.customer?.id || existing?.customerId || undefined,
    customer: editor.customer || existing?.customer || undefined,
    reference: editor.reference || '',
    theirReference: editor.theirRef || '',
    comment: editor.comment || '',
    paymentSource,
    settlementAccountCode,
    ...payment,
    status,
    attachments: existing?.attachments || undefined,
    createdAt: existing?.createdAt || nowIso(),
    updatedAt: nowIso(),
    source: 'plumme',
  });
};

const upsertOtherSale = async (sale) => {
  if (!sale?.id) throw new Error(t('svc.missingOtherSaleSave'));
  const existing = safeArray(runtime.appState?.otherSales).find((entry)=>entry.id === sale.id);
  assertSimpleVatMutationAllowed({
    previousDate: existing?.date,
    nextDate: sale.date,
    vatAffects: !existing || existing.date !== sale.date || num(existing.amountNet) !== num(sale.amountNet) || String(existing.vatTreatment || '') !== String(sale.vatTreatment || '') || globalThis.PlummeOtherSaleAmounts.vatSignature(existing) !== globalThis.PlummeOtherSaleAmounts.vatSignature(sale),
  });
  const result = await authorizedPost('otherSaleMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'upsert',
    sale,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  } else if (result.sale) {
    runtime.appState = mergeState({
      ...mergeState(runtime.appState),
      otherSales: [result.sale, ...safeArray(runtime.appState?.otherSales).filter((entry) => entry.id !== result.sale.id)],
    });
    emitRuntime();
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.sale || sale;
};

const annulOtherSale = async (saleOrId, options = {}) => {
  const saleId = typeof saleOrId === 'string' ? saleOrId : saleOrId?.id;
  if (!saleId) throw new Error(t('svc.missingOtherSaleCancel'));
  const existing = safeArray(runtime.appState?.otherSales).find((entry)=>entry.id === saleId);
  assertSimpleVatMutationAllowed({previousDate: existing?.date, nextDate: existing?.date, vatAffects: true});
  const result = await authorizedPost('otherSaleMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'annul',
    saleId,
    annulComment: options.annulComment || options.comment || '',
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.sale || null;
};

const deleteOtherSaleDraft = async (saleOrId) => {
  const saleId = typeof saleOrId === 'string' ? saleOrId : saleOrId?.id || saleOrId?._backend?.id;
  if (!saleId) throw new Error(t('svc.missingOtherSaleDelete'));
  const existing = safeArray(runtime.appState?.otherSales).find((entry)=>entry.id === saleId);
  if (existing?.status !== 'draft') throw new Error(t('svc.onlyOtherSaleDrafts'));
  const result = await authorizedPost('otherSaleMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'delete-draft',
    saleId,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  window.dispatchEvent(new Event(BACKEND_EVENTS.drafts));
  return true;
};

const purchaseCategoryAccountCode = (category = '') => {
  const key = String(category || '').toLowerCase();
  if (key.includes('råvarer') || key.includes('halvfabrikata')) return '4000';
  if (key.includes('driftsmidler') || key.includes('utstyr')) return '6500';
  if (key.includes('husleie')) return '6300';
  if (key.includes('telefon') || key.includes('internett')) return '6900';
  if (key.includes('programvare') || key.includes('abonnement')) return '6540';
  if (key.includes('reise') || key.includes('diett')) return '7140';
  if (key.includes('representasjon')) return '7350';
  return '7700';
};

const normalizePurchasePaymentSource = (settlementAccountCode) => {
  const code = String(settlementAccountCode || '');
  if (settlementAccountCode === 'private' || /^206\d$/.test(code)) return 'private';
  if (code === '1900') return 'cash';
  if (code === '2250') return 'loan';
  if (/^238\d$/.test(code)) return 'company_card';
  if (/^158\d$/.test(code)) return 'payment_solution';
  return 'company';
};

const purchaseSettlementAccountCode = (settlementAccountCode) => {
  if (settlementAccountCode === 'private') return '2060';
  if (/^206\d$/.test(String(settlementAccountCode || ''))) return String(settlementAccountCode);
  if (settlementAccountCode === '1900') return '1900';
  if (settlementAccountCode === '2250') return '2250';
  return settlementAccountCode || '1920';
};
const purchaseVoucherNumberFromId = (id, isSupplierInvoice) => `${isSupplierInvoice ? 'LF' : 'KJ'}-${String(id || Date.now()).replace(/^expense-/, '').slice(-6).toUpperCase()}`;

const receiptAttachmentsForPurchase = (receipt) => {
  const attachments = safeArray(receipt?.attachments);
  if (attachments.length) return attachments;
  if (!receipt?.file && !receipt?.downloadUrl) return [];
  return [stripUndefinedDeep({
    id: receipt.attachmentId || receipt.id,
    name: receipt.attachmentName || receipt.name || 'Bilag',
    mimeType: receipt.attachmentMimeType || receipt.mimeType || '',
    downloadUrl: receipt.downloadUrl || receipt.file,
    storagePath: receipt.storagePath,
  })];
};

// Resolve only a saved, active supplier; ambiguous names require explicit selection.
const findSupplierRecordByName = (name, state, id) => {
  const wanted = normalizedPartyName(name);
  const matches = safeArray((state || runtime.appState)?.suppliers).filter(supplier =>
    supplier.id && !['archived','passiv'].includes(supplier.status) && (id ? supplier.id === id : wanted && normalizedPartyName(supplier.name) === wanted));
  return matches.length === 1 ? matches[0] : null;
};

// Dobbelforing fanges paa dato og sum, slik Fiken gjor det. Dokumenttypen holdes
// utenfor med vilje: det samme kjopet kan vaere fort inn som vanlig kjop den ene
// gangen og som leverandorfaktura den andre. Leverandornavnet avgjor ikke treffet,
// det gjor bare varselet tydeligere naar det stemmer.
const RECEIPT_DUPLICATE_AMOUNT_TOLERANCE = 0.5;
const findPossibleDuplicateExpenses = ({ date, grossAmount, supplierName, excludeExpenseId } = {}) => {
  const isoDate = String(date || '').slice(0, 10);
  const gross = Math.abs(num(grossAmount));
  if (!isoDate || !(gross > 0)) return [];
  const wanted = normalizedPartyName(supplierName);
  return safeArray(runtime.appState?.expenses)
    .filter((expense) => isActiveExpense(expense) && !expense.annulledAt && expense.id !== excludeExpenseId)
    .filter((expense) => String(expense.date || '').slice(0, 10) === isoDate)
    .filter((expense) => Math.abs(dashboardGrossExpense(expense) - gross) <= RECEIPT_DUPLICATE_AMOUNT_TOLERANCE)
    .map((expense) => ({
      id: expense.id,
      voucherNumber: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      date: expense.date || '',
      year: Number(String(expense.date || '').slice(0, 4)) || null,
      grossAmount: dashboardGrossExpense(expense),
      supplierName: expense.supplierName || '',
      description: expense.description || '',
      isSupplierInvoice: Boolean(expense.isSupplierInvoice),
      sameSupplier: Boolean(wanted) && normalizedPartyName(expense.supplierName) === wanted,
    }));
};

const buildPurchaseFromEditor = (editor = {}, status = 'active') => {
  const country = currentCountry();
  const settings = mergeState(runtime.appState).settings;
  const purchaseDate = editor.date || todayIso();
  const registeredFrom = /^\d{4}-\d{2}-\d{2}$/.test(String(settings.vatRegisteredFrom || '')) ? settings.vatRegisteredFrom : '';
  const deregisteredFrom = !settings.vatRegistered && /^\d{4}-\d{2}-\d{2}$/.test(String(settings.vatDeregisteredFrom || '')) ? settings.vatDeregisteredFrom : '';
  const isVatRegistered = registeredFrom
    ? purchaseDate >= registeredFrom && (!deregisteredFrom || purchaseDate < deregisteredFrom)
    : settings.vatRegistered === true && (!deregisteredFrom || purchaseDate < deregisteredFrom);
  const isFixedAsset = editor.kjopType === 'Eiendel' || editor.isFixedAsset;
  const fixedAssetConfig = isFixedAsset ? fixedAssetGroupConfig(editor.fixedAssetGroup || editor.group || 'd') : null;
  const rawLines = safeArray(editor.lines).filter((line) =>
    String(line.desc || line.description || '').trim() || num(line.net ?? line.amountNet) > 0,
  );
  if (status !== 'draft' && rawLines.some((line) => !String(line.desc || line.description || '').trim())) throw new Error(t('r1e.purchaseEditor.descriptionRequired'));
  const lines = rawLines.map((line, index) => {
    const requestedPurchaseVatCode = country === 'NO' && isVatRegistered && ['14','21','23'].includes(String(line.purchaseVatCode || '')) ? String(line.purchaseVatCode) : '';
    const vatRate = requestedPurchaseVatCode === '23' ? 0 : isVatRegistered ? num(line.vat, line.vatRate ?? standardVatForCountry(country)) : 0;
    const reverseChargeCode = country === 'NO' && line.reverse === '86' ? line.reverse : undefined;
    // The visible special code has one valid treatment. Repair stale companion
    // metadata from older drafts before the strict backend consistency check.
    const vatTreatment = requestedPurchaseVatCode === '14'
      ? 'vatOnly'
      : ['21','23'].includes(requestedPurchaseVatCode)
        ? 'importBasis'
        : reverseChargeCode ? 'reverseCharge' : isVatRegistered && vatRate > 0 ? 'standard' : 'zeroRated';
    const purchaseVatCode = ['vatOnly','importBasis'].includes(vatTreatment) ? requestedPurchaseVatCode : '';
    const standardVatCode = purchaseVatCode === '23' ? '20' : purchaseVatCode;
    return stripUndefinedDeep({
      id: line.id || `line-${index + 1}-${Date.now()}`,
      description: String(line.desc || line.description || editor.description || 'Kjøp').trim(),
      amountNet: Math.max(0, num(line.net ?? line.amountNet)),
      vatAmount: vatTreatment === 'standard' && line.vatAmount !== undefined && line.vatAmount !== null && line.vatAmount !== ''
        ? Math.max(0, num(line.vatAmount))
        : undefined,
      vatRate,
      vatTreatment,
      purchaseVatCode: purchaseVatCode || undefined,
      standardVatCode: standardVatCode || undefined,
      reverseChargeCode,
      vatDeductionMode: !isVatRegistered ? 'none' : ['none','partial'].includes(String(line.vatDeductionMode || '')) ? String(line.vatDeductionMode) : 'full',
      deductibleVatPercent: !isVatRegistered ? 0 : String(line.vatDeductionMode || '') === 'none' ? 0 : String(line.vatDeductionMode || '') === 'partial' ? Math.max(0, Math.min(100, num(line.deductibleVatPercent))) : 100,
      costAccountCode: vatTreatment === 'vatOnly' ? undefined : line.costAccountCode || fixedAssetConfig?.assetAccountCode || purchaseCategoryAccountCode(line.kat || line.category),
      category: vatTreatment === 'vatOnly' ? undefined : line.kat || line.category || '',
      productId: vatTreatment === 'vatOnly' ? undefined : line.productId || undefined,
      inventoryQuantity: vatTreatment !== 'vatOnly' && line.productId ? Math.max(0, num(line.inventoryQuantity ?? line.quantity)) : undefined, ...window.PlummeInvoicePurchaseFields.purchaseLineAccountingFields(line, isVatRegistered),
    });
  });
  if (status !== 'draft' && !lines.length && !receiptAttachmentsForPurchase(editor.receipt).length) {
    throw new Error(t('svc.addPurchaseLine'));
  }
  const amountNet = lines.reduce((sum, line) => sum + num(line.amountNet), 0);
  const primaryLine = lines[0] || {};
  // Prefer the selected saved identity; legacy inbox drafts may only contain a name.
  const supplier = findSupplierRecordByName(editor.supplierName, undefined, editor.supplierId);
  const settlementAccountCode = purchaseSettlementAccountCode(editor.payAcct);
  const roundingAmount = Math.round(num(editor.roundingAmount) * 100) / 100;
  const gross = Math.round((lines.reduce((sum, line) => {
    const vat = line.vatTreatment === 'vatOnly'
      ? num(line.amountNet)
      : line.vatTreatment === 'standard' && line.vatAmount !== undefined
      ? num(line.vatAmount)
      : line.vatTreatment === 'standard' ? num(line.amountNet) * num(line.vatRate) / 100 : 0;
    return sum + (line.vatTreatment === 'vatOnly' ? vat : num(line.amountNet) + (line.reverseChargeCode ? 0 : vat));
  }, 0) + roundingAmount) * 100) / 100;
  const basePaymentSplits = safeArray(editor.partPayments)
    .filter((payment) => num(payment.amount) > 0)
    .map((payment, index) => stripUndefinedDeep({
      id: payment.id || `split-${index + 1}-${Date.now()}`,
      amount: Math.min(num(payment.amount), gross),
      date: payment.date || editor.payDate || editor.date || todayIso(),
      paymentSource: normalizePurchasePaymentSource(payment.account || payment.settlementAccountCode || settlementAccountCode),
      settlementAccountCode: purchaseSettlementAccountCode(payment.account || payment.settlementAccountCode || settlementAccountCode),
    }));
  const paidAmount = basePaymentSplits.reduce((sum, payment) => sum + num(payment.amount), 0);
  const isPartialPayment = basePaymentSplits.length > 0 && paidAmount < gross - 0.005;
  const isSupplierInvoice = editor.kjopType === 'Registrer faktura' || isPartialPayment || !editor.isPaid;
  if (status !== 'draft' && editor.kjopType === 'Vanlig kjøp' && !String(editor.payAcct || '').trim()) {
    throw new Error(t('svc.purchasePaymentAccountRequired'));
  }
  if (status !== 'draft' && editor.kjopType === 'Vanlig kjøp' && !String(editor.payDate || '').trim()) {
    throw new Error(t('svc.purchasePaymentDateRequired'));
  }
  const paymentSplits = basePaymentSplits.length && isPartialPayment
    ? [...basePaymentSplits, { id: `split-payable-${Date.now()}`, amount: gross - paidAmount, settlementAccountCode: '2400', paymentSource: 'supplier_invoice' }]
    : basePaymentSplits;
  return stripUndefinedDeep({
    id: editor.id || `expense-${Date.now()}`,
    voucherNumber: editor.voucherNumber || purchaseVoucherNumberFromId(editor.id, isSupplierInvoice),
    date: editor.date || todayIso(),
    // En leverandorfaktura uten forfallsdato ble liggende uten frist i
    // alderfordelingen. Star ingen frist paa dokumentet, forfaller den samme dag
    // som den er datert.
    dueDate: isSupplierInvoice ? (editor.dueDate || editor.date || todayIso()) : undefined,
    description: editor.description || primaryLine.description || supplier?.name || editor.supplierName || 'Kjøp',
    amountNet,
    documentGrossAmount: editor.documentGrossAmount !== undefined ? num(editor.documentGrossAmount) : undefined,
    roundingAmount: roundingAmount || undefined,
    roundingAccountCode: roundingAmount ? (editor.roundingAccountCode || '7740') : undefined,
    vatRate: primaryLine.vatRate ?? 0,
    vatTreatment: primaryLine.vatTreatment || 'zeroRated',
    purchaseVatCode: primaryLine.purchaseVatCode,
    standardVatCode: primaryLine.standardVatCode,
    vatDeductionMode: isVatRegistered ? 'full' : 'none', deductibleVatPercent: isVatRegistered ? 100 : 0,
    costAccountCode: primaryLine.vatTreatment === 'vatOnly' ? undefined : primaryLine.costAccountCode || fixedAssetConfig?.assetAccountCode || '7700',
    purchaseLines: lines,
    fixedAsset: fixedAssetConfig
      ? {
          ...(editor.fixedAsset || {}),
          assetName: String(editor.fixedAssetName || editor.name || editor.description || primaryLine.description || 'Driftsmiddel').trim(),
          assetGroup: fixedAssetConfig.id,
          depreciationRate: fixedAssetConfig.rate,
          assetAccountCode: fixedAssetConfig.assetAccountCode,
          depreciationAccountCode: fixedAssetConfig.depreciationAccountCode,
          accumulatedDepreciationAccountCode: fixedAssetConfig.accumulatedDepreciationAccountCode,
          status: editor.fixedAsset?.status || 'active',
        }
      : undefined,
    reverseChargeCode: lines.find((line) => line.reverseChargeCode)?.reverseChargeCode,
    isSupplierInvoice,
    supplierId: supplier?.id || editor.supplierId || undefined,
    supplierName: supplier?.name || editor.supplierName || undefined,
    supplierOrganizationNumber: supplier?.organizationNumber || supplier?.org || editor.supplierOrganizationNumber || undefined,
    supplierInvoiceNumber: editor.invoiceNumber || undefined,
    supplierInvoicePaidAt: isSupplierInvoice && editor.isPaid && !isPartialPayment ? (editor.payDate || editor.date || todayIso()) : undefined,
    paidAt: !isSupplierInvoice && editor.isPaid ? (editor.payDate || editor.date || todayIso()) : undefined,
    paidBy: editor.isPaid || !isSupplierInvoice ? normalizePurchasePaymentSource(editor.payAcct) : undefined,
    settlementAccountCode: editor.isPaid || !isSupplierInvoice ? settlementAccountCode : undefined,
    paymentSplits,
    attachmentId: editor.receipt?.attachmentId,
    attachmentName: editor.receipt?.attachmentName || editor.receipt?.name,
    attachmentMimeType: editor.receipt?.attachmentMimeType || editor.receipt?.mimeType,
    attachments: receiptAttachmentsForPurchase(editor.receipt),
    // Kurssporet folger bilaget, ikke bare innbokslinjen: det er kjopet som er
    // bilaget, og omregningskursen maa fremgaa av det.
    currency: editor.currency || undefined,
    originalTotalGross: editor.currency && editor.currency !== 'NOK' ? num(editor.originalTotalGross) || undefined : undefined,
    totalGrossNok: editor.currency && editor.currency !== 'NOK' ? num(editor.totalGrossNok) || undefined : undefined,
    exchangeRate: editor.exchangeRate || undefined,
    exchangeRateDate: editor.exchangeRateDate || undefined,
    exchangeRateSource: editor.exchangeRateSource || undefined,
    receiptInboxEntryId: editor.receipt?.id,
    // Prosjektkoblingen kommer enten fra innboksraden bilaget ble bokfort fra,
    // eller fra prosjektvelgeren i kjopsskjemaet.
    ...projectCostFields({
      projectId: editor.projectId || editor.receipt?.projectId,
      projectBillable: editor.projectBillable ?? editor.receipt?.projectBillable,
      projectInvoiceStatus: editor.projectInvoiceStatus,
      projectInvoiceId: editor.projectInvoiceId,
      projectInvoicedAt: editor.projectInvoicedAt,
    }),
    createdAt: editor.createdAt || nowIso(),
    updatedAt: nowIso(),
    status,
    source: 'plumme',
  });
};

const upsertPurchaseLocal = async (expense) => {
  if (!expense?.id) throw new Error(t('svc.missingPurchaseSave'));
  const normalized = await uploadEditorAttachments(expense, safeArray(expense.attachments), 'expenses');
  const gross = dashboardGrossExpense(normalized);
  const supplierPaymentSplits = normalized.status === 'draft' ? [] : normalized.isSupplierInvoice
    ? safeArray(normalized.paymentSplits).filter((split) => split.settlementAccountCode !== '2400' && num(split.amount) > 0)
    : [];
  const supplierPaymentId = (index, trace) => {
    const statementId = String(trace?.statementId || '').trim();
    const lineId = String(trace?.lineId || '').trim();
    return statementId && lineId
      ? `payment-${normalized.id}-${statementId}-${lineId}-${index + 1}`
      : `payment-${normalized.id}-${index + 1}-${Date.now()}`;
  };
  const supplierPaymentVoucherNumber = (index) => `${normalized.voucherNumber || normalized.supplierInvoiceNumber || normalized.id}-BET${index ? `-${index + 1}` : ''}`;
  const payments = supplierPaymentSplits.length
    ? supplierPaymentSplits.map((split, index) => { const bankTrace = split.bankTrace || normalized.bankTrace; return stripUndefinedDeep({
        id: supplierPaymentId(index, bankTrace),
        voucherNumber: supplierPaymentVoucherNumber(index),
        documentType: 'supplier_invoice',
        documentId: normalized.id,
        direction: 'outgoing',
        amount: Math.min(num(split.amount), gross),
        date: split.date || normalized.supplierInvoicePaidAt || normalized.date,
        settlementAccountCode: purchaseSettlementAccountCode(split.settlementAccountCode || normalized.settlementAccountCode),
        reference: normalized.supplierInvoiceNumber || normalized.description,
        status: 'active',
        bankTrace,
        createdAt: nowIso(),
      })})
    : normalized.status !== 'draft' && normalized.isSupplierInvoice && normalized.supplierInvoicePaidAt
      ? [stripUndefinedDeep({
        id: supplierPaymentId(0, normalized.bankTrace),
        voucherNumber: supplierPaymentVoucherNumber(0),
        documentType: 'supplier_invoice',
        documentId: normalized.id,
        direction: 'outgoing',
        amount: gross,
        date: normalized.supplierInvoicePaidAt,
        settlementAccountCode: purchaseSettlementAccountCode(normalized.settlementAccountCode),
        reference: normalized.supplierInvoiceNumber || normalized.description,
        status: 'active',
        bankTrace: normalized.bankTrace,
        createdAt: nowIso(),
      })]
      : [];
  await updateStateAndPersist((state) => {
    const nextReceiptInbox = normalized.status === 'draft'
      ? safeArray(state.receiptInbox)
      : safeArray(state.receiptInbox).map((entry) =>
          entry.id === normalized.receiptInboxEntryId
            ? stripUndefinedDeep({ ...entry, status: 'booked', bookedAt: nowIso(), bookedAs: 'expense', linkedEntryId: normalized.id })
            : entry,
        );
    return {
      ...state,
      expenses: [normalized, ...safeArray(state.expenses).filter((entry) => entry.id !== normalized.id)],
      payments: [
        ...payments,
        ...safeArray(state.payments).filter((payment) =>
          !(payment.documentType === 'supplier_invoice' && payment.documentId === normalized.id),
        ),
      ],
      receiptInbox: nextReceiptInbox,
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  return normalized;
};

const upsertPurchase = async (expense) => {
  if (!expense?.id) throw new Error(t('svc.missingPurchaseSave'));
  const existing = safeArray(runtime.appState?.expenses).find((entry)=>entry.id === expense.id);
  assertPurchaseMutationAllowed({previousExpense: existing, nextExpense: expense, action: 'upsert'});
  const normalized = await uploadEditorAttachments(expense, safeArray(expense.attachments), 'expenses');
  try {
    const result = await authorizedPost('purchaseMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'upsert',
      expense: normalized,
    });
    if (result?.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
      window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
    }
    return result.expense || normalized;
  } catch (error) {
    if (normalized.purchaseCurrencyVersion || !shouldFallbackToClientStateWrite(error)) throw error;
    return upsertPurchaseLocal(normalized);
  }
};

const getPurchaseDraftsModel = () => {
  const state = mergeState(runtime.appState);
  return safeArray(state.expenses)
    .filter((expense) => expense?.status === 'draft' && !expense.manualPosting)
    .map((expense) => {
      const type = expense.fixedAsset ? 'Eiendel' : expense.isSupplierInvoice ? 'Registrer faktura' : 'Vanlig kjøp';
      const firstLine = safeArray(expense.purchaseLines)[0] || {};
      return {
        id: expense.id,
        supplier: expense.supplierName || '',
        descr: expense.description || firstLine.description || '',
        kjopType: type,
        fakturanr: expense.supplierInvoiceNumber || '',
        date: expense.date ? isoToNb(expense.date) : '',
        isoDate: expense.date || '',
        amount: Math.round(dashboardGrossExpense(expense)),
        source: expense,
      };
    })
    .sort((a, b) => String(b.isoDate || '').localeCompare(String(a.isoDate || '')));
};

const deletePurchaseDraftLocal = async (expenseId) => {
  if (!expenseId) throw new Error(t('svc.missingDraftDelete'));
  const existing = safeArray(runtime.appState?.expenses).find((entry) => entry.id === expenseId);
  if (!existing) throw new Error(t('svc.draftNotFound'));
  if (existing.status !== 'draft') throw new Error(t('svc.onlyPurchaseDrafts'));
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: safeArray(state.expenses).filter((entry) => entry.id !== expenseId),
    payments: safeArray(state.payments).filter((payment) =>
      !(payment.documentType === 'supplier_invoice' && payment.documentId === expenseId),
    ),
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return existing;
};

const deletePurchaseDraft = async (expenseOrId) => {
  const expenseId = typeof expenseOrId === 'string' ? expenseOrId : expenseOrId?.id;
  if (!expenseId) throw new Error(t('svc.missingDraftDelete'));
  const existing = safeArray(runtime.appState?.expenses).find((entry) => entry.id === expenseId);
  if (existing?.status !== 'draft') throw new Error(t('svc.onlyPurchaseDrafts'));
  try {
    const result = await authorizedPost('purchaseMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'delete-draft',
      expenseId,
    });
    if (result?.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    return result.expense || existing;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return deletePurchaseDraftLocal(expenseId);
  }
};

const annulPurchaseLocal = async (expenseId, options = {}) => {
  const existing = safeArray(runtime.appState?.expenses).find((entry)=>entry.id === expenseId);
  assertPurchaseMutationAllowed({previousExpense: existing, nextExpense: existing, action: 'annul'});
  const now = nowIso();
  const comment = String(options.annulComment || options.comment || '').trim();
  const linkedReceiptId = existing?.receiptInboxEntryId || '';
  const reopenedLineNote = comment || 'Kjøpet er reversert. Kontroller banklinjen på nytt.';
  let nextExpense = null;
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: safeArray(state.expenses).map((expense) => {
      if (expense.id !== expenseId) return expense;
      nextExpense = stripUndefinedDeep({ ...expense, status: 'annulled', annulledAt: expense.annulledAt || now, annulComment: comment || expense.annulComment, updatedAt: now });
      return nextExpense;
    }),
    payments: safeArray(state.payments).map((payment) =>
      payment.documentType === 'supplier_invoice' && payment.documentId === expenseId
        ? stripUndefinedDeep({ ...payment, status: 'reversed', reversedAt: payment.reversedAt || now, updatedAt: now })
        : payment,
    ),
    receiptInbox: linkedReceiptId
      ? safeArray(state.receiptInbox).map((entry) => entry.id === linkedReceiptId
          ? stripUndefinedDeep({ ...entry, status: 'ready', bookedAt: undefined, bookedAs: undefined, linkedEntryId: undefined })
          : entry)
      : safeArray(state.receiptInbox),
    bankStatements: safeArray(state.bankStatements).map((statement) => ({
      ...statement,
      lines: safeArray(statement.lines).map((line) => {
        if (line.matchedEntryId !== expenseId) return line;
        const nextLine = { ...line };
        ['matchedEntryId', 'matchedEntryType', 'bankTrace'].forEach((key) => { delete nextLine[key]; });
        return stripUndefinedDeep({ ...nextLine, reconciliationStatus: 'manual-review', note: reopenedLineNote });
      }),
    })),
  }));
  if (!nextExpense) throw new Error(t('svc.purchaseToCancelNotFound'));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return nextExpense;
};

const annulPurchase = async (expenseOrId, options = {}) => {
  const expenseId = typeof expenseOrId === 'string' ? expenseOrId : expenseOrId?.id;
  if (!expenseId) throw new Error(t('svc.missingPurchaseCancel'));
  const existing = safeArray(runtime.appState?.expenses).find((entry)=>entry.id === expenseId);
  assertPurchaseMutationAllowed({previousExpense: existing, nextExpense: existing, action: 'annul'});
  try {
    const result = await authorizedPost('purchaseMutationHttp', {
      businessId: runtime.activeBusinessId,
      action: 'annul',
      expenseId,
      annulComment: options.annulComment || options.comment || '',
    });
    if (result.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return result.expense || null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return annulPurchaseLocal(expenseId, options);
  }
};

const buildFixedAssetReclassifiedPurchase = (expense, options = {}) => {
  if (!expense?.fixedAsset) throw new Error(t('svc.assetToUpdateNotFound'));
  const expenseAccountCode = String(options.expenseAccountCode || '').trim();
  if (!/^\d{4}$/.test(expenseAccountCode) || !/^[4-8]/.test(expenseAccountCode)) throw new Error(t('svc.pickExpenseAccount'));
  const purchaseLines = safeArray(expense.purchaseLines).length
    ? safeArray(expense.purchaseLines)
    : [{ id: 'line-1', description: expense.description || expense.fixedAsset?.assetName || 'Kj\u00f8p', amountNet: expense.amountNet || 0, vatRate: expense.vatRate ?? 25, vatTreatment: expense.vatTreatment }];
  return stripUndefinedDeep({
    ...expense,
    fixedAsset: undefined,
    isFixedAsset: false,
    kjopType: 'Vanlig kj\u00f8p',
    costAccountCode: expenseAccountCode,
    categoryAccountCode: expenseAccountCode,
    category: options.expenseAccountName || expense.category || 'Kostnad',
    purchaseLines: purchaseLines.map((line) => stripUndefinedDeep({
      ...line,
      costAccountCode: expenseAccountCode,
      category: options.expenseAccountName || line.category,
    })),
    reclassifiedFromFixedAssetAt: options.reclassifiedAt || nowIso(),
    reclassifiedFromFixedAssetComment: String(options.comment || options.reclassComment || '').trim() || undefined,
    updatedAt: nowIso(),
  });
};

const reclassifyFixedAssetPurchase = async (expenseOrId, options = {}) => {
  const expenseId = typeof expenseOrId === 'string' ? expenseOrId : expenseOrId?.id;
  if (!expenseId) throw new Error(t('svc.missingAsset'));
  return mutateFixedAsset({operation:'reclassify_purchase',assetId:expenseId,date:options.date||todayIso(),expenseAccountCode:options.expenseAccountCode,...(options.reverseDepreciation?{reverseDepreciation:true}:{}),note:String(options.comment||'').trim()});
};

const safeFilePart = (value, fallback = 'faktura') =>
  String(value || fallback)
    .trim()
    .replace(/[^\p{L}\p{N}._-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120) || fallback;

const pdfBase64ToBlob = (pdfBase64) => {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: 'application/pdf' });
};

const getInvoicePdfFromBackend = async (invoice, settings, variant, options = {}) => {
  const token = await runtime.auth.currentUser?.getIdToken();
  if (!token) throw new Error(t('svc.mustBeSignedIn3'));
  const response = await fetch(functionUrl('downloadInvoicePdfHttp'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(await window.PlummeAppCheck?.headers?.()) },
    body: JSON.stringify({
      invoice,
      settings,
      variant,
      businessId: runtime.activeBusinessId,
      storePdf: !!options.storePdf,
      followUpPaymentSummary: options.followUpPaymentSummary || null,
    }),
  });
  const responseText = await response.text();
  const payload = responseText.trim() ? JSON.parse(responseText) : null;
  if (!response.ok) throw new Error(payload?.message || payload?.error || responseText || `HTTP ${response.status}`);
  if (!payload?.pdfBase64 || !payload?.fileName) throw new Error(t('svc.noPdfReturned'));
  return payload;
};

const getShareholders = () => window.PlummeAnnualMeetings?.normalizeShareholders?.(mergeState(runtime.appState).shareholders) || [];

const saveShareholders = async (list = []) => {
  const shareholders = window.PlummeAnnualMeetings?.normalizeShareholders?.(list) || [];
  await updateStateAndPersist((state) => ({ ...state, shareholders }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return shareholders;
};

const getAnnualMeetings = (year) =>
  window.PlummeAnnualMeetings?.normalizeMeetings?.(mergeState(runtime.appState).annualMeetingsByYear?.[year]) || {};

const saveAnnualMeeting = async ({ year, documentKey, patch = {} } = {}) => {
  const meetings = window.PlummeAnnualMeetings;
  if (!meetings || !meetings.MEETING_KEYS.includes(documentKey)) return null;
  let saved = null;
  await updateStateAndPersist((state) => {
    const byYear = state.annualMeetingsByYear || {};
    const current = meetings.normalizeMeetings(byYear[year]);
    saved = meetings.normalizeMeeting({ ...current[documentKey], ...patch });
    return {
      ...state,
      annualMeetingsByYear: { ...byYear, [year]: { ...current, [documentKey]: saved } },
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const backendPdfStorageInfo = (pdf, invoice) => ({
  storagePath: pdf?.storagePath || '',
  downloadUrl: pdf?.downloadUrl || '',
  fileName: safeFilePart(pdf?.fileName, `${invoice?.invoiceNumber || invoice?.id || 'faktura'}.pdf`),
});

const uploadEditorAttachments = async (document, attachments = [], ownerType = 'invoices') => {
  const existing = safeArray(document.attachments);
  const fresh = safeArray(attachments).filter((attachment) => attachment?.file instanceof File);
  const carried = safeArray(attachments)
    .filter((attachment) => !(attachment?.file instanceof File))
    .map((attachment) => stripUndefinedDeep({
      id: attachment.id || `att${Date.now()}`,
      name: attachment.name || 'Vedlegg',
      mimeType: attachment.type || attachment.mimeType || '',
      storagePath: attachment.storagePath,
      downloadUrl: attachment.downloadUrl,
      source: attachment.source || 'plumme',
    }));
  const mergeAttachmentMeta = (items) => Array.from(new Map(items.map((item) => [item.id || item.storagePath || item.name, item])).values());
  if (!fresh.length) return stripUndefinedDeep({ ...document, attachments: mergeAttachmentMeta([...existing, ...carried]) });
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const uploaded = [];
  for (const attachment of fresh) {
    if (Number(attachment.file.size || 0) > 15 * 1024 * 1024) throw new Error(t('svc.attachmentMax15'));
    const cleanFileName = safeFilePart(attachment.name, 'vedlegg');
    const storagePath = `businesses/${runtime.activeBusinessId}/${ownerType}/${document.id}/attachments/${Date.now()}-${cleanFileName}`;
    const ref = runtime.storage.ref().child(storagePath);
    await ref.put(attachment.file, {
      contentType: attachment.type || attachment.file.type || 'application/octet-stream',
      customMetadata: {
        businessId: runtime.activeBusinessId,
        documentId: document.id || '',
        ownerType,
        source: 'plumme',
      },
    });
    const downloadUrl = await ref.getDownloadURL().catch(() => '');
    uploaded.push(stripUndefinedDeep({
      id: attachment.id || `att${Date.now()}`,
      name: cleanFileName,
      mimeType: attachment.type || attachment.file.type || '',
      storagePath,
      downloadUrl: downloadUrl || undefined,
      source: 'plumme',
      uploadedAt: nowIso(),
    }));
  }
  return stripUndefinedDeep({ ...document, attachments: mergeAttachmentMeta([...existing, ...carried, ...uploaded]) });
};

// Company files use business Storage and settings.companyDocuments metadata.
const companyDocumentsModel = () => globalThis.PlummeCompanyDocuments;

// Storage-reglene tillater bare disse typene under `businesses/`. Et dokument
// vi ikke far lastet opp ville uansett aldri naadd kunden.
const allowedCompanyDocumentMimeTypes = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
]);
const COMPANY_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
const COMPANY_DOCUMENT_MAX_COUNT = 25;

const companyDocumentsFromState = (state = runtime.appState) =>
  companyDocumentsModel()?.normalizeDocuments(mergeState(state).settings?.companyDocuments) || [];

const getCompanyDocuments = () => companyDocumentsFromState();

const persistCompanyDocuments = async (mapper) => {
  let saved = [];
  await updateStateAndPersist((state) => {
    const current = companyDocumentsModel()?.normalizeDocuments(state.settings?.companyDocuments) || [];
    saved = companyDocumentsModel()?.normalizeDocuments(mapper(current)) || [];
    return {
      ...state,
      settings: {
        ...(state.settings || {}),
        companyDocuments: saved.map((document) => stripUndefinedDeep(document)),
      },
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const uploadCompanyDocument = async (file, meta = {}) => {
  const uploadBusinessId = runtime.activeBusinessId;
  if (!(file instanceof File)) throw new Error(t('svc.pickFileFirst'));
  const mimeType = file.type || '';
  if (!allowedCompanyDocumentMimeTypes.has(mimeType)) throw new Error(t('svc.companyDocumentFileType'));
  if (Number(file.size || 0) > COMPANY_DOCUMENT_MAX_BYTES) throw new Error(t('svc.attachmentMax10'));
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (runtime.activeBusinessId !== uploadBusinessId) throw new Error(t('svc.reload'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  if (companyDocumentsFromState().length >= COMPANY_DOCUMENT_MAX_COUNT) throw new Error(t('svc.companyDocumentMaxCount'));

  const id = createId('company-doc');
  const cleanFileName = safeFilePart(file.name || 'dokument');
  const storagePath = `businesses/${runtime.activeBusinessId}/company-documents/${id}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: mimeType || 'application/octet-stream',
    customMetadata: {
      businessId: runtime.activeBusinessId,
      documentId: id,
      source: 'company-document',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const document = companyDocumentsModel()?.normalizeDocument({
    id,
    name: String(meta.name || '').trim() || cleanFileName.replace(/\.[^.]+$/, ''),
    documentType: meta.documentType,
    fileName: cleanFileName,
    mimeType,
    size: file.size,
    storagePath,
    downloadUrl,
    uploadedAt: nowIso(),
    updatedAt: nowIso(),
    targets: meta.targets,
  });
  if (!document) throw new Error(t('svc.companyDocumentSaveFailed'));
  if (runtime.activeBusinessId !== uploadBusinessId) throw new Error(t('svc.reload'));
  await persistCompanyDocuments((documents) => [document, ...documents]);
  return document;
};

const saveCompanyDocument = async (documentId, patch = {}) => {
  const id = String(documentId || '').trim();
  if (!id) throw new Error(t('svc.companyDocumentNotFound'));
  let saved = null;
  await persistCompanyDocuments((documents) => documents.map((document) => {
    if (document.id !== id) return document;
    saved = {
      ...document,
      name: String(patch.name ?? document.name).trim() || document.name,
      documentType: patch.documentType ?? document.documentType,
      targets: patch.targets ? companyDocumentsModel()?.normalizeTargets(patch.targets) : document.targets,
      updatedAt: nowIso(),
    };
    return saved;
  }));
  if (!saved) throw new Error(t('svc.companyDocumentNotFound'));
  return saved;
};

const deleteCompanyDocument = async (documentId) => {
  const id = String(documentId || '').trim();
  const target = companyDocumentsFromState().find((document) => document.id === id);
  if (!target) throw new Error(t('svc.companyDocumentNotFound'));
  await persistCompanyDocuments((documents) => documents.filter((document) => document.id !== id));
  if (target.storagePath && runtime.storage) {
    await runtime.storage.ref().child(target.storagePath).delete().catch(() => undefined);
  }
  return { deletedDocumentId: id };
};

const resolveCompanyDocumentUrl = async (document) => {
  if (document?.downloadUrl) return document.downloadUrl;
  if (document?.storagePath && runtime.storage) {
    return runtime.storage.ref().child(document.storagePath).getDownloadURL().catch(() => '');
  }
  return '';
};

const openCompanyDocument = async (document) => {
  const url = await resolveCompanyDocumentUrl(document);
  if (!url) throw new Error(t('svc.docFileNotFound'));
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Vedleggene salgsflyten skal starte med for en gitt dokumenttype. De peker paa
// filer som allerede ligger i Storage, saa editoren laster ingenting opp paa nytt.
const getDefaultSaleAttachments = (docType) =>
  companyDocumentsModel()?.toAttachments(
    companyDocumentsModel()?.selectForSaleDocType(companyDocumentsFromState(), docType) || [],
  ) || [];

const allowedEmployeeDocumentExtensions = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']);
const allowedEmployeeDocumentMimeTypes = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
]);

const assertAllowedEmployeeDocumentFile = (file) => {
  const extension = String(file?.name || '').split('.').pop()?.toLowerCase() || '';
  if (allowedEmployeeDocumentExtensions.has(extension)) return;
  if (file?.type && allowedEmployeeDocumentMimeTypes.has(file.type)) return;
  throw new Error(t('svc.allowedFileTypes'));
};

const replaceEmployeeInState = (state, employeeId, mapper) => {
  let nextEmployee = null;
  const employees = safeArray(state.employees).map((employee) => {
    if (employee.id !== employeeId) return employee;
    nextEmployee = mapper(employee);
    return nextEmployee;
  });
  if (!nextEmployee) throw new Error(t('svc.employeeNotFound'));
  return { nextState: { ...state, employees }, nextEmployee };
};

const uploadEmployeeDocument = async (employeeId, file, description = '') => {
  if (!employeeId) throw new Error(t('svc.missingEmployee'));
  if (!(file instanceof File)) throw new Error(t('svc.pickFileFirst'));
  assertAllowedEmployeeDocumentFile(file);
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));

  const id = createId('emp-doc');
  const cleanFileName = safeFilePart(file.name || 'ansattdokument');
  const storagePath = `businesses/${runtime.activeBusinessId}/employees/${employeeId}/documents/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      businessId: runtime.activeBusinessId,
      employeeId,
      source: 'plumme',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const document = stripUndefinedDeep({
    id,
    name: cleanFileName,
    mimeType: file.type || '',
    size: file.size,
    description: String(description || '').trim() || undefined,
    uploadedAt: nowIso(),
    storagePath,
    downloadUrl: downloadUrl || undefined,
  });
  let savedEmployee = null;
  await updateStateAndPersist((state) => {
    const result = replaceEmployeeInState(state, employeeId, (employee) => stripUndefinedDeep({
      ...employee,
      documents: [...safeArray(employee.documents), document],
      updatedAt: nowIso(),
    }));
    savedEmployee = result.nextEmployee;
    return result.nextState;
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return { employee: mapEmployeeToUi(savedEmployee), document };
};

const resolveEmployeeDocumentUrl = async (document) => {
  if (document?.downloadUrl) return document.downloadUrl;
  if (document?.storagePath && runtime.storage) {
    return runtime.storage.ref().child(document.storagePath).getDownloadURL().catch(() => '');
  }
  return '';
};

const openEmployeeDocument = async (document) => {
  const url = await resolveEmployeeDocumentUrl(document);
  if (!url) throw new Error(t('svc.docFileNotFound'));
  window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadEmployeeDocument = async (document) => {
  const url = await resolveEmployeeDocumentUrl(document);
  if (!url) throw new Error(t('svc.docFileNotFound'));
  await downloadUrlAsFile(url, document.name || 'ansattdokument');
};

const deleteEmployeeDocument = async (employeeId, documentId) => {
  if (!employeeId || !documentId) throw new Error(t('svc.missingDocDelete'));
  let targetDocument = null;
  let savedEmployee = null;
  await updateStateAndPersist((state) => {
    const result = replaceEmployeeInState(state, employeeId, (employee) => {
      targetDocument = safeArray(employee.documents).find((document) => document.id === documentId) || null;
      if (!targetDocument) throw new Error(t('svc.docNotFound'));
      return stripUndefinedDeep({
        ...employee,
        documents: safeArray(employee.documents).filter((document) => document.id !== documentId),
        updatedAt: nowIso(),
      });
    });
    savedEmployee = result.nextEmployee;
    return result.nextState;
  });
  if (targetDocument?.storagePath && runtime.storage) {
    await runtime.storage.ref().child(targetDocument.storagePath).delete().catch(() => undefined);
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return { employee: mapEmployeeToUi(savedEmployee), deletedDocumentId: documentId };
};
